import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { App as CapacitorApp } from "@capacitor/app";
import { resolveScreen } from "../../services/analyticsScreens";
import {
  logScreenView,
  setAnalyticsUserId,
  setAnalyticsUserProperty,
} from "../../services/analytics";
import { trackFunnel } from "../../services/funnelTracker";

/**
 * Reports every screen the user reaches to Firebase Analytics.
 *
 * Headless — renders nothing, mounts once inside <IonReactRouter> next to the
 * other global handlers. It is the single source of `screen_view` events in the
 * app; native automatic screen reporting is disabled because in a Capacitor
 * WebView it can only ever report the one host controller/activity.
 *
 * @see services/analyticsScreens.js for the route -> screen name table.
 */
const AnalyticsScreenTracker = () => {
  const { pathname } = useLocation();

  const userId = useSelector((state) => state.user?.user?.id);
  const tier = useSelector(
    (state) =>
      state.user?.master_club_subscriber?.subscription?.subscription_type?.name
  );

  // Last reported screen, replayed when the app returns to the foreground.
  const currentScreenRef = useRef(null);

  useEffect(() => {
    const { screenName, screenClass } = resolveScreen(pathname);

    // Ionic keeps pages mounted and re-renders on unrelated location changes
    // (search/hash, state pushes). Only report an actual screen change.
    if (currentScreenRef.current?.screenName === screenName) return;

    currentScreenRef.current = { screenName, screenClass };
    logScreenView(screenName, screenClass);

    // Second sink: the CRM funnel. Reusing this component's existing screen
    // resolution rather than adding a hook to each of the five wizard steps —
    // it already derives the step number from the route (`onboarding_step_N`),
    // and one place that cannot drift is worth more than five that can.
    //
    // Only the wizard's own screens are reported here. Everything else in the
    // funnel is an ACTION rather than a view (a button tapped, an enrolment
    // completed) and is fired from the component that performs it, because a
    // screen being on display is not the same as the member doing anything.
    const onboardingStep = /^onboarding_step_(\d+)$/.exec(screenName);
    if (onboardingStep) {
      trackFunnel("onboarding_step_viewed", { step: Number(onboardingStep[1]) });
    }
  }, [pathname]);

  // Firebase opens a NEW session when the app comes back after its session
  // timeout (30 min default). Without this, that session's events carry no
  // screen at all and the engagement lands on "(not set)".
  useEffect(() => {
    let disposed = false;
    let handle;

    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (disposed || !isActive) return;
      const screen = currentScreenRef.current;
      if (screen) logScreenView(screen.screenName, screen.screenClass);
    })
      .then((listener) => {
        handle = listener;
        if (disposed) listener.remove();
      })
      .catch(() => {});

    return () => {
      disposed = true;
      handle?.remove();
    };
  }, []);

  // Identity: lets Firebase collapse the same person across devices and
  // reinstalls in the active-user counts, and segment reports by plan.
  // Internal numeric id only — never email/phone/name (Firebase forbids PII).
  useEffect(() => {
    setAnalyticsUserId(userId ?? null);
  }, [userId]);

  useEffect(() => {
    setAnalyticsUserProperty("user_tier", tier || "guest");
  }, [tier]);

  return null;
};

export default AnalyticsScreenTracker;
